import { useTranslation } from 'react-i18next';
import { LANG_OPTIONS } from '@/i18n';
import { Globe } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const current = LANG_OPTIONS.find(l => l.code === i18n.language) || LANG_OPTIONS[1];

  const changeLang = async (code) => {
    await i18n.changeLanguage(code);
    // Persist on the user profile so the preference follows them across devices/browsers.
    // Silently ignore failures (e.g. not authenticated yet) — localStorage still keeps the choice locally.
    if (user && (user.id || user._id)) {
      try { await api.patch('/auth/me/language', { language: code }); } catch { /* ignore */ }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm border border-zinc-800 bg-zinc-900/80 hover:bg-zinc-800 transition-colors text-sm text-zinc-200"
          data-testid="lang-switcher"
        >
          <Globe className="w-3.5 h-3.5 text-zinc-500" />
          <span>{current.flag}</span>
          {!compact && <span className="text-xs uppercase tracking-wider">{current.code}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-zinc-100" align="end">
        {LANG_OPTIONS.map(opt => (
          <DropdownMenuItem
            key={opt.code}
            onClick={() => changeLang(opt.code)}
            className={`gap-2 cursor-pointer ${i18n.language === opt.code ? 'bg-zinc-800/60' : ''}`}
            data-testid={`lang-option-${opt.code}`}
          >
            <span className="text-lg">{opt.flag}</span>
            <span>{opt.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
